import React, { useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  ListSubheader
} from '@mui/material';

const CONDITION_TYPES = [
  { value: 'flag_present', label: 'Flag Present', requiresDomain: true, requiresOperator: false },
  { value: 'domain_score', label: 'Domain Score', requiresDomain: true, requiresOperator: true },
  { value: 'total_score', label: 'Total Score', requiresDomain: false, requiresOperator: true },
  { value: 'flag_count', label: 'Flag Count', requiresDomain: false, requiresOperator: true },
  { value: 'assessment_band', label: 'Assessment Band', requiresDomain: false, requiresOperator: true }
];

// Default domains - will be supplemented by availableDomains prop
const DEFAULT_DOMAINS = [
  'depression',
  'anxiety',
  'psychosis',
  'trauma',
  'ptsd',
  'dissociation',
  'self-harm',
  'selfharm',
  'suicidality',
  'suicide-ideation',
  'substance-use',
  'eating-disorder',
  'relationship-issues',
  'grief-loss'
];

const OPERATORS = {
  numeric: ['==', '!=', '<', '<=', '>', '>=', 'between'],
  categorical: ['==', '!=', 'in', 'not_in']
};

const BANDS = ['low', 'mild', 'moderate', 'high', 'severe'];

const CUSTOM_DOMAIN_VALUE = '__custom__';

const ConditionRow = ({ condition, onChange, availableDomains = [] }) => {
  const conditionType = CONDITION_TYPES.find(t => t.value === condition.type) || CONDITION_TYPES[0];

  // Merge default domains with available domains from DB
  const allDomains = [...new Set([...DEFAULT_DOMAINS, ...availableDomains])].sort();

  // Check if current domain is custom (not in the list)
  const isCustomDomain = condition.domain && !allDomains.includes(condition.domain);
  const [showCustomInput, setShowCustomInput] = useState(isCustomDomain);
  const [customDomainValue, setCustomDomainValue] = useState(isCustomDomain ? condition.domain : '');

  useEffect(() => {
    // Ensure condition has default values including operator (required by Mongoose schema)
    if (!condition.type) {
      onChange({ ...condition, type: 'flag_present', domain: DEFAULT_DOMAINS[0], operator: '==', value: true });
    } else if (!condition.operator) {
      // Ensure operator is always set (required by Mongoose schema)
      onChange({ ...condition, operator: '==' });
    }
  }, []);

  const handleTypeChange = (newType) => {
    const typeConfig = CONDITION_TYPES.find(t => t.value === newType);
    const newCondition = {
      type: newType,
      domain: typeConfig.requiresDomain ? (condition.domain || DEFAULT_DOMAINS[0]) : undefined,
      value: newType === 'flag_present' ? true : newType === 'assessment_band' ? BANDS[0] : 0,
      // Mongoose schema ALWAYS requires operator, even for flag_present
      operator: '==',
      range: undefined,
      values: undefined
    };
    onChange(newCondition);
  };

  const handleFieldChange = (field, value) => {
    onChange({ ...condition, [field]: value });
  };

  const handleDomainChange = (value) => {
    if (value === CUSTOM_DOMAIN_VALUE) {
      setShowCustomInput(true);
      // Don't change the domain yet - wait for user to type
    } else {
      setShowCustomInput(false);
      setCustomDomainValue('');
      handleFieldChange('domain', value);
    }
  };

  const handleCustomDomainChange = (value) => {
    setCustomDomainValue(value);
    if (value.trim()) {
      handleFieldChange('domain', value.trim().toLowerCase().replace(/\s+/g, '-'));
    }
  };

  const isNumericCondition = ['domain_score', 'total_score', 'flag_count'].includes(condition.type);

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
      <FormControl sx={{ minWidth: 200 }}>
        <InputLabel>Condition Type</InputLabel>
        <Select
          value={condition.type || 'flag_present'}
          onChange={(e) => handleTypeChange(e.target.value)}
          label="Condition Type"
          size="small"
        >
          {CONDITION_TYPES.map(type => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {conditionType.requiresDomain && (
        <>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Domain</InputLabel>
            <Select
              value={showCustomInput || isCustomDomain ? CUSTOM_DOMAIN_VALUE : (condition.domain || '')}
              onChange={(e) => handleDomainChange(e.target.value)}
              label="Domain"
              size="small"
            >
              {allDomains.map(domain => (
                <MenuItem key={domain} value={domain}>
                  {domain.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </MenuItem>
              ))}
              <ListSubheader sx={{ backgroundColor: '#f5f5f5' }}>Other</ListSubheader>
              <MenuItem value={CUSTOM_DOMAIN_VALUE}>
                <em>Custom domain...</em>
              </MenuItem>
            </Select>
          </FormControl>
          {(showCustomInput || isCustomDomain) && (
            <TextField
              label="Custom Domain"
              value={customDomainValue}
              onChange={(e) => handleCustomDomainChange(e.target.value)}
              placeholder="e.g., ocd, bipolar"
              size="small"
              sx={{ minWidth: 150 }}
              helperText="Lowercase, use hyphens"
            />
          )}
        </>
      )}

      {conditionType.requiresOperator && (
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Operator</InputLabel>
          <Select
            value={condition.operator || '=='}
            onChange={(e) => handleFieldChange('operator', e.target.value)}
            label="Operator"
            size="small"
          >
            {(isNumericCondition ? OPERATORS.numeric : OPERATORS.categorical).map(op => (
              <MenuItem key={op} value={op}>
                {op}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {condition.operator !== 'between' && isNumericCondition && (
        <TextField
          type="number"
          label="Value"
          value={condition.value || 0}
          onChange={(e) => handleFieldChange('value', Number(e.target.value))}
          sx={{ width: 100 }}
          size="small"
        />
      )}

      {condition.operator === 'between' && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            type="number"
            label="Min"
            value={condition.range?.[0] ?? 0}
            onChange={(e) => {
              const min = Number(e.target.value);
              const max = condition.range?.[1] ?? 100;
              handleFieldChange('range', [min, max]);
            }}
            sx={{ width: 80 }}
            size="small"
          />
          <Typography variant="body2">to</Typography>
          <TextField
            type="number"
            label="Max"
            value={condition.range?.[1] ?? 100}
            onChange={(e) => {
              const min = condition.range?.[0] ?? 0;
              const max = Number(e.target.value);
              handleFieldChange('range', [min, max]);
            }}
            sx={{ width: 80 }}
            size="small"
          />
        </Box>
      )}

      {condition.type === 'assessment_band' && (
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Band</InputLabel>
          <Select
            value={condition.value || BANDS[0]}
            onChange={(e) => handleFieldChange('value', e.target.value)}
            label="Band"
            size="small"
          >
            {BANDS.map(band => (
              <MenuItem key={band} value={band}>
                {band.charAt(0).toUpperCase() + band.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>
  );
};

export default ConditionRow;
